import type { ValidationResult, ValidationError, IntegrationParams } from '../types';

// Import integrators registry from the package main export (JSON is inlined in the bundle)
import { integratorsRegistry } from '@almadar/patterns';

interface ActionParam {
  name: string;
  type: string;
  required?: boolean;
  description?: string;
}

interface ActionDef {
  name: string;
  description?: string;
  params: ActionParam[];
}

interface IntegratorEntry {
  name: string;
  description?: string;
  category?: string;
  actions: ActionDef[];
}

type IntegratorsRegistry = Record<string, {
  version?: string;
  exportedAt?: string;
  integrators: Record<string, IntegratorEntry>;
}>;

/**
 * Validate action params against registry schema
 */
export function validateParams(
  integration: string,
  action: string,
  params: IntegrationParams,
): ValidationResult {
  const typedRegistry = integratorsRegistry as IntegratorsRegistry[string];
  const registry = typedRegistry.integrators[integration];

  if (!registry) {
    return {
      valid: false,
      errors: [
        {
          param: 'integration',
          message: `Unknown integration: ${integration}`,
        },
      ],
    };
  }

  const actionDef = registry.actions.find((a: ActionDef) => a.name === action);

  if (!actionDef) {
    return {
      valid: false,
      errors: [{ param: 'action', message: `Unknown action: ${action}` }],
    };
  }

  const errors: ValidationError[] = [];

  // Check required params
  for (const paramDef of actionDef.params) {
    if (paramDef.required && !(paramDef.name in params)) {
      errors.push({
        param: paramDef.name,
        message: `Missing required parameter: ${paramDef.name}`,
      });
    }

    // Type validation
    if (paramDef.name in params) {
      const value = params[paramDef.name];
      const expectedType = paramDef.type;
      const actualType = typeof value;

      if (expectedType === 'number' && actualType !== 'number') {
        errors.push({
          param: paramDef.name,
          message: `Expected ${expectedType}, got ${actualType}`,
        });
      }

      if (expectedType === 'string' && actualType !== 'string') {
        errors.push({
          param: paramDef.name,
          message: `Expected ${expectedType}, got ${actualType}`,
        });
      }

      if (expectedType === 'array' && !Array.isArray(value)) {
        errors.push({
          param: paramDef.name,
          message: `Expected array, got ${actualType}`,
        });
      }

      if (
        expectedType === 'object' &&
        (actualType !== 'object' || Array.isArray(value) || value === null)
      ) {
        errors.push({
          param: paramDef.name,
          message: `Expected object, got ${actualType}`,
        });
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
